import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dumbbell, Sun, CreditCard, CalendarDays } from 'lucide-react';
import { ClientShell } from '../components/ClientShell';
import { exercisesLabel } from '../components/DaySelector';
import { sessionUnits, unitsLabel } from '../lib/sessionUnits';
import { formatSets } from '../components/WorkoutLogFeed';
import { MyReports } from './WeeklyReportPage';
import {
  useClientData, activePlanOf, activePackageOf, nextWorkoutDay, dayLabel, moodLabel, formatDate,
} from '../hooks/useClientData';

const zl = (v) => `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const HISTORY_ICONS = { workout: Dumbbell, entry: Sun, payment: CreditCard, session: CalendarDays };

// Client "Panel": which workout when, when to pay, and history. Nothing else.
export const ClientPanelPage = () => {
  const navigate = useNavigate();
  const { plans, monitoring, packages, sessions, workouts, loading } = useClientData();
  const [showHistory, setShowHistory] = useState(false);

  const plan = activePlanOf(plans);
  const pkg = activePackageOf(packages);
  const now = new Date().toISOString();

  const planDays = useMemo(() => {
    if (!plan) return [];
    const next = nextWorkoutDay(plan, workouts);
    const days = [...new Set(plan.exercises.map((e) => e.day_number))].sort((a, b) => a - b);
    return days.map((d) => ({
      day: d,
      label: dayLabel(plan, d),
      count: plan.exercises.filter((e) => e.day_number === d).length,
      isNext: d === next,
    }));
  }, [plan, workouts]);

  const upcomingSessions = sessions
    .filter((s) => s.status === 'scheduled' && s.start_time >= now)
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .slice(0, 3);

  const history = useMemo(() => {
    const items = [
      ...workouts.map((w) => ({
        kind: 'workout', date: w.log_date,
        title: `Trening · ${plan && plan.id === w.plan_id ? dayLabel(plan, w.day_number) : `Dzień ${w.day_number}`}`,
        sub: [w.duration_minutes ? `${w.duration_minutes} min` : 'zrobiony',
          (w.trainer_comment || w.exercises?.some((e) => e.trainer_comment)) && 'komentarz trenera'].filter(Boolean).join(' · '),
        log: w,
      })),
      ...monitoring.map((m) => ({
        kind: 'entry', date: m.log_date,
        title: `Samopoczucie: ${moodLabel(m.mood) || '—'}`,
        sub: [m.pain_level != null && `ból ${m.pain_level}`, m.sleep_hours != null && `sen ${String(m.sleep_hours).replace('.', ',')} h`].filter(Boolean).join(' · ') || 'wpis',
      })),
      ...packages.filter((p) => p.status !== 'cancelled').map((p) => ({
        kind: 'payment', date: p.purchased_date, title: `Płatność · ${p.package_label}`, sub: zl(p.price),
      })),
      ...sessions.filter((s) => s.status === 'completed').map((s) => ({
        kind: 'session', date: s.start_time.slice(0, 10), title: 'Sesja z trenerem',
        sub: sessionUnits(s) > 1 ? `odbyta · ${unitsLabel(sessionUnits(s))}` : 'odbyta',
      })),
    ];
    return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 15);
  }, [workouts, monitoring, packages, sessions, plan]);

  const sessionsLeft = pkg && pkg.sessions_total != null ? pkg.sessions_total - pkg.sessions_used : null;

  return (
    <ClientShell>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 flex flex-col gap-6 sm:gap-8" data-testid="client-panel">
        <h1 className="big-num text-[34px] sm:text-6xl">Panel</h1>

        {loading ? (
          <div className="text-[var(--text-muted)] py-12">Ładowanie...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 items-start">
            <div className="flex flex-col gap-5 lg:gap-6">
              <section className="glass rounded-[28px] p-6 sm:p-8 flex flex-col gap-3" data-testid="panel-plan">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1 sm:gap-3">
                  <h2 className="text-lg sm:text-xl font-extrabold tracking-tight">Mój plan</h2>
                  {plan && <span className="text-xs text-[var(--text-muted)]">{plan.plan_name} · {plan.frequency}× w tyg.</span>}
                </div>
                {!plan ? (
                  <p className="text-sm text-[var(--text-muted)]">Nie masz jeszcze aktywnego planu.</p>
                ) : (
                  <>
                    {planDays.map((d) => (
                      <div key={d.day} className={`flex items-center gap-4 p-3.5 rounded-2xl ${d.isNext ? 'border border-[#C9A84C]/45 bg-[#C9A84C]/[0.07]' : 'glass-soft'}`}>
                        <span className={`text-sm font-extrabold w-16 shrink-0 ${d.isNext ? 'text-[#C9A84C]' : ''}`}>{d.isNext ? 'Następny' : `Dzień ${d.day}`}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-bold text-[15px] leading-snug">{d.label}</span>
                          <span className="block text-xs text-[var(--text-muted)] mt-0.5">{exercisesLabel(d.count)}</span>
                        </span>
                      </div>
                    ))}
                    <button onClick={() => navigate(`/client-plan/${plan.id}`)} className="cta-ghost h-11 rounded-full text-xs font-semibold tracking-[0.2em] uppercase mt-1">
                      Cały plan
                    </button>
                  </>
                )}
                {upcomingSessions.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    <p className="label-text text-[var(--text-muted)] text-[0.6rem]">Sesje z trenerem</p>
                    {upcomingSessions.map((s) => (
                      <div key={s.id} className="flex justify-between text-sm">
                        <span>{formatDate(s.start_time, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        <span className="text-[var(--text-muted)]">{new Date(s.start_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}{s.location ? ` · ${s.location}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="glass rounded-[28px] p-6 sm:p-8 flex flex-col gap-4" data-testid="panel-payment">
                <h2 className="text-lg sm:text-xl font-extrabold tracking-tight">Płatność</h2>
                {!pkg ? (
                  <p className="text-sm text-[var(--text-muted)]">Brak aktywnego pakietu. Zapytaj trenera o kolejny.</p>
                ) : (
                  <>
                    <div className="flex justify-between items-end gap-4">
                      <div>
                        <p className="label-text text-[var(--text-muted)] text-[0.6rem]">Następna płatność</p>
                        <p className="big-num text-3xl mt-2.5" data-testid="payment-due">
                          {pkg.payment_due_date ? formatDate(pkg.payment_due_date, { day: 'numeric', month: 'short' }) : '—'}
                        </p>
                        <p className="text-xs text-[var(--text-muted)] mt-1.5">
                          {pkg.payment_due_date ? `${pkg.package_label} · ${zl(pkg.price)}` : 'termin ustali trener'}
                        </p>
                      </div>
                      {pkg.sessions_total != null && (
                        <div className="text-right">
                          <p className="label-text text-[var(--text-muted)] text-[0.6rem]">Pakiet</p>
                          <p className="big-num text-3xl mt-2.5">{pkg.sessions_used}<span className="text-base text-[var(--text-muted)]">/{pkg.sessions_total}</span></p>
                          <p className="text-xs text-[var(--text-muted)] mt-1.5">sesji</p>
                        </div>
                      )}
                    </div>
                    {pkg.sessions_total != null && (
                      <>
                        <div className="h-1.5 rounded-full bg-[var(--track)] overflow-hidden">
                          <div className="h-full rounded-full bg-[var(--chart-main)]" style={{ width: `${(pkg.sessions_used / pkg.sessions_total) * 100}%` }} />
                        </div>
                        <p className="text-xs text-[var(--text-muted)]">Zostały {sessionsLeft} {sessionsLeft === 1 ? 'sesja' : sessionsLeft >= 2 && sessionsLeft <= 4 ? 'sesje' : 'sesji'}.</p>
                      </>
                    )}
                  </>
                )}
              </section>
            </div>

            <MyReports />

            <section className="glass rounded-[28px] p-6 sm:p-8 flex flex-col gap-2.5" data-testid="panel-history">
              <h2 className="text-lg sm:text-xl font-extrabold tracking-tight mb-1">Historia</h2>
              {history.length === 0 && <p className="text-sm text-[var(--text-muted)]">Jeszcze nic tu nie ma.</p>}
              {/* Collapsed by default: the list is long and the panel should stay short */}
              {history.length > 0 && (
                <button onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}
                  className="cta-ghost h-11 rounded-full text-xs font-semibold tracking-[0.2em] uppercase" data-testid="toggle-history">
                  {showHistory ? 'Ukryj historię' : `Pokaż historię (${history.length})`}
                </button>
              )}
              {showHistory && history.map((h, i) => {
                const Icon = HISTORY_ICONS[h.kind];
                if (h.log) {
                  // A workout opens to show every set, the client's notes and the trainer's comments.
                  return (
                    <details key={i} className="glass-soft rounded-2xl px-3.5 py-3" data-testid="client-workout-history">
                      <summary className="flex items-center gap-3.5 cursor-pointer list-none">
                        <span className="w-9 h-9 rounded-full bg-[var(--track)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" strokeWidth={1.7} /></span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold truncate">{h.title}</span>
                          <span className={`block text-xs mt-0.5 ${h.sub.includes('komentarz') ? 'text-[#C9A84C]' : 'text-[var(--text-muted)]'}`}>{h.sub}</span>
                        </span>
                        <span className="text-xs text-[var(--text-muted)] shrink-0">{formatDate(h.date)}</span>
                      </summary>
                      <div className="mt-3 pl-12 flex flex-col gap-1.5 text-xs">
                        {h.log.exercises.map((ex, j) => (
                          <div key={j}>
                            <p><span className="font-semibold">{ex.exercise_name}</span>{formatSets(ex) && <span className="text-[var(--text-muted)]"> — {formatSets(ex)}</span>}</p>
                            {ex.notes && <p className="text-[var(--text-muted)]">Ty: „{ex.notes}”</p>}
                            {ex.trainer_comment && <p className="text-[#C9A84C]">Trener: „{ex.trainer_comment}”</p>}
                          </div>
                        ))}
                        {h.log.overall_notes && <p className="text-[var(--text-muted)] mt-1">Ty: „{h.log.overall_notes}”</p>}
                        {h.log.trainer_comment && <p className="text-[#C9A84C] mt-1 text-sm">Trener: „{h.log.trainer_comment}”</p>}
                      </div>
                    </details>
                  );
                }
                return (
                  <div key={i} className="glass-soft rounded-2xl px-3.5 py-3 flex items-center gap-3.5">
                    <span className="w-9 h-9 rounded-full bg-[var(--track)] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" strokeWidth={1.7} /></span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold truncate">{h.title}</span>
                      <span className="block text-xs text-[var(--text-muted)] mt-0.5">{h.sub}</span>
                    </span>
                    <span className="text-xs text-[var(--text-muted)] shrink-0">{formatDate(h.date)}</span>
                  </div>
                );
              })}
            </section>
          </div>
        )}
      </main>
    </ClientShell>
  );
};
